import * as React from "react";
import { useState } from "react";
import { alpha } from "@mui/material/styles";
import { Accordion, AccordionDetails, AccordionSummary, Alert, AlertTitle, Box, Button, Card, CardContent, Grid, Stack, TextField, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import dayjs from "dayjs";
import { getPeriodLockStatus, getPersonalInfo } from "../../store/integration-process/selector";
import { useAppDispatch, useAppSelector } from "../../Hooks";
import { getUserInfo } from "../../store/user-process/selector";
import LocalStorageProvider from "../../utils/locaStorageProvider";
import { AlertType, LocalStorageMethod } from "../../enums/enum";
import { WriteIpuReadings } from "../../services/api-actions/integration-action";
import { setShowAllert } from "../../store/user-process/user-process";
import { IShowAlert } from "../../types/user-info/user-info";
import PageLayout from "../../Components/page-layout/PageLayout";
import { brand } from "../../theme";

function Notice({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" gap={1.5} alignItems="center">
          <InfoOutlinedIcon sx={{ color: brand.main }} />
          <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Typography color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.6 }}>
          {children}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}

export default function SendReadings() {
  const dispatch = useAppDispatch();
  const userInfo = useAppSelector(getUserInfo);
  const lockPeriod = useAppSelector(getPeriodLockStatus);
  let defaultLic = LocalStorageProvider(userInfo.userId, null, LocalStorageMethod.getItem);
  const [data] = useState([
    {
      TypePu: "",
      indinication: "",
    },
  ]);
  const [eror, setEror] = useState({
    TypePu: "",
    Eror: false,
    Message: "",
  });
  const counters = useAppSelector(getPersonalInfo)?.counters;

  return (
    <PageLayout title="Передача показаний" subtitle="Передавайте показания до 25 числа каждого месяца включительно">
      {lockPeriod == true && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Период передачи показаний закрыт</AlertTitle>
          Мы обязаны использовать в расчетах только показания, переданные до 25-го числа включительно. Поэтому данные, которые Вы передадите сейчас, могут быть не приняты к
          расчету за текущий месяц.
        </Alert>
      )}

      {counters?.length == 0 && (
        <Card sx={{ textAlign: "center", py: 5, px: 2, mb: 3 }}>
          <Typography variant="h6">У вас нет приборов учёта</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            По выбранному лицевому счёту не найдено счётчиков, по которым можно передать показания.
          </Typography>
        </Card>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {counters?.length > 0 &&
          counters.map((item, index) => {
            const expired = GetStatus(item.dateCheckNext);
            const hasError = eror.TypePu == item.typePu ? eror.Eror : false;
            return (
              <Grid item xs={12} md={6} key={item.factoryNumber ?? index}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Stack direction="row" gap={1.5} alignItems="center" sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "12px",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: alpha(brand.main, 0.1),
                          color: brand.main,
                        }}
                      >
                        <WaterDropOutlinedIcon />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" sx={{ lineHeight: 1.25 }}>
                          {item.typePu}
                        </Typography>
                        <Typography color="text.secondary" sx={{ fontSize: 13.5 }}>
                          № {item.factoryNumber}
                          {item.dateCheckNext != null ? ` · поверка до ${dayjs(item.dateCheckNext).format("DD.MM.YYYY")}` : ""}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between" sx={{ p: 1.5, borderRadius: 2, bgcolor: brand.background, mb: 2 }}>
                      <Typography color="text.secondary" sx={{ fontSize: 14 }}>
                        Предыдущие показания
                        {item.lastReadingDate != null ? ` (${dayjs(item.lastReadingDate).format("DD.MM.YYYY")})` : ""}
                      </Typography>
                      <Typography sx={{ fontWeight: 700 }}>{item.lastReadings}</Typography>
                    </Stack>

                    {expired ? (
                      <Alert severity="error">Срок поверки счётчика закончился — показания по нему не принимаются. Обратитесь для поверки или замены прибора учёта.</Alert>
                    ) : (
                      <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} alignItems={{ xs: "stretch", sm: "flex-start" }}>
                        <TextField
                          error={hasError}
                          label="Новые показания"
                          type="number"
                          fullWidth
                          inputProps={{ inputMode: "decimal", min: item.lastReadings }}
                          helperText={hasError ? `Не меньше предыдущих: ${item.lastReadings}` : " "}
                          onChange={(e: any) => {
                            let pu = data.find((x) => x.TypePu == item.typePu);

                            if (pu == undefined) {
                              data.push({ TypePu: item.typePu, indinication: e.target.value });
                            } else {
                              pu.indinication = e.target.value;
                            }
                            pu = data.find((x) => x.TypePu == item.typePu);
                            if (pu != undefined && Number(pu.indinication) < item.lastReadings) {
                              setEror({ Eror: true, TypePu: item.typePu, Message: "" });
                            } else {
                              setEror({ Eror: false, TypePu: "", Message: "" });
                            }
                          }}
                        />
                        {lockPeriod != true && (
                          <Button
                            variant="contained"
                            size="large"
                            sx={{ flexShrink: 0, height: 56 }}
                            onClick={() => {
                              let pu = data.find((x) => x.TypePu == item.typePu);
                              if (pu != undefined && Number(pu.indinication) >= item.lastReadings) {
                                dispatch(WriteIpuReadings({ lic: defaultLic.lic, typePu: pu.TypePu, readings: Number(pu.indinication) }));
                              } else {
                                dispatch(setShowAllert({ open: true, message: "Новые показания меньше предыдущих", type: AlertType.error } as IShowAlert));
                              }
                            }}
                          >
                            Передать
                          </Button>
                        )}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
      </Grid>

      <Notice title="Сроки передачи и расчёт по среднему">
        Уважаемый Абонент! В соответствии с Правилами предоставления коммунальных услуг показания счетчиков должны передаваться в Филиал "Пензенский" ПАО "Т Плюс" не позднее 25
        числа каждого месяца. В случае передачи показаний после 25 числа, расчет потребленного объема услуг будет произведен исходя из среднего потребления за предыдущие 6
        месяцев, а переданные показания будут учтены в следующем расчетном периоде для корректировки начисленного по среднему объему. При непредоставлении потребителем
        показаний индивидуального или общего (квартирного) прибора учета за текущий месяц начисления будут рассчитаны исходя из среднего объема, но не более 3 месяцев подряд,
        далее - по нормативам потребления коммунальных услуг. При недопуске 2 и более раз потребителем в занимаемое им помещение для проверки состояния установленных и
        введенных в эксплуатацию индивидуальных, общих (квартирных) приборов учёта, проверки достоверности представленных сведений о показаниях таких приборов учета, плата за
        коммунальные услуги рассчитывается исходя из нормативов потребления коммунальных услуг с применением повышающих коэффициентов.
      </Notice>
      <Notice title="Поверка счётчиков">
        Обратите внимание на номер и дату поверки счетчика (ИПУ) горячей воды/ отопления (при наличии). При несоответствии информации сообщите верную информацию, приложив фото
        счетчика, паспорт счетчика, акт ввода счетчика/ акт поверки счетчика. В соответствии с Правилами предоставления коммунальных услуг в случае истечения срока эксплуатации
        ИПУ, определяемого периодом времени до очередной поверки в течение 3-х месяцев расчет платы будет производиться исходя из среднемесячного объема потребления горячей
        воды. По истечению 3-х месяцев расчет будет производиться исходя из нормативов потребления коммунальной услуги по горячей воде с применением повышающего коэффициента,
        величина которого принимается равной 1,5. Оснащение помещения ИПУ, ввод в эксплуатацию ИПУ на основании заявки, надлежащая техническая эксплуатация, сохранность и
        своевременная замена либо проведение поверки ИПУ должны быть обеспечены собственником помещения.
      </Notice>
    </PageLayout>
  );
}

// true — срок поверки уже истёк
function GetStatus(date: string | undefined): boolean {
  if (!date) return false;

  const formats = ["DD.MM.YYYY", "YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY"];
  const dateCheckNext = dayjs(date, formats, true);

  if (!dateCheckNext.isValid()) {
    console.error("Некорректный формат даты:", date);
    return false;
  }

  return dateCheckNext.isBefore(dayjs(), "day");
}
